import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { FormGroup, ControlLabel, FormControl, InputGroup, HelpBlock } from 'react-bootstrap'
import HelpPopover from '../HelpPopover'
import Select from '../Select'

const defaultFormat = value => value == null ? '' : value

const getSelectedValues = options => {
  const result = []
  if (options) {
    for (let index = 0; index < options.length; index++) {
      const option = options[index]
      if (option.selected) {
        result.push(option.value)
      }
    }
  }
  return result
}

const getValue = (event) => {
  const {
    target: { type, value, checked, files },
    dataTransfer
  } = event
  if (type === 'checkbox') {
    return !!checked
  }
  if (type === 'file') {
    return files || (dataTransfer && dataTransfer.files)
  }
  if (type === 'select-multiple') {
    return getSelectedValues(event.target.options)
  }
  return value
}

const onChangeValue = (event, { name, parse }) => {
  let newValue = getValue(event)
  if (parse) {
    newValue = parse(newValue, name)
  }

  return newValue
}

class Field extends Component {
  constructor(props) {
    super(props)

    this.state = {
      initialValue: null,
      currentValue: props.value,
      calculatedValue: props.calculatedValue,
      hasFocus: false,
      keepEmpty: false,
      error: null,
      touched: false,
    }

    this.handleOnBlur = this.handleOnBlur.bind(this)
    this.handleOnFocus = this.handleOnFocus.bind(this)
  }

  componentDidMount() {
    this.setState({ initialValue: this.props.value })
  }

  componentWillReceiveProps(nextProps) {
    // Use parse function to parse the next values.
    const parsedNextValue = this.props.parse && nextProps.value ? this.props.parse(nextProps.value, nextProps.name) : nextProps.value
    const parsedNextCalculatedValue = this.props.parse && nextProps.calculatedValue
      ? this.props.parse(nextProps.calculatedValue, nextProps.name)
      : nextProps.calculatedValue

    let newState = {}

    if (parsedNextValue !== this.state.currentValue) {
      newState.currentValue = parsedNextValue

      // Run validator(s)
      this.validate(parsedNextValue || parsedNextCalculatedValue, this.state.touched)

      if (this.state.hasFocus) {
        newState.keepEmpty = true
      }
    }

    if (parsedNextCalculatedValue !== this.state.calculatedValue) {
      newState.calculatedValue = parsedNextCalculatedValue
    }

    this.setState(newState)
  }

  handleOnFocus() {
    const { currentValue, calculatedValue } = this.state

    const newState = { hasFocus: true }

    if (currentValue === 0 && !calculatedValue) {
      newState.currentValue = null
    }

    this.setState(newState)
  }

  handleOnBlur() {
    const { currentValue, initialValue, calculatedValue } = this.state

    const newState = { hasFocus: false, keepEmpty: false, touched: true }

    if (currentValue === null) {
      newState.currentValue = calculatedValue || initialValue
    }

    // Run validator(s) now that "touched" is true
    this.validate(currentValue || calculatedValue, true)

    this.setState(newState)
  }

  validate(value, forceTouched = null) {
    const { validator, onValidation, name } = this.props

    const touched = forceTouched !== null ? forceTouched : this.state.touched

    if (validator) {
      let validators = Array.isArray(validator) ? validator : [validator]

      for (const func of validators) {
        const error = func(value, touched)

        // If result is undefined, there is no error. Otherwise, error is returned.
        if (typeof error !== 'undefined') {
          this.setState({error})

          // Run callback if prop passed in.
          if (onValidation) {
            onValidation(name, error)
          }

          return false
        }
      }
    }

    // No errors, so reset the error state.
    this.setState({ error: null })

    // Run callback if prop passed in.
    if (onValidation) {
      onValidation(name)
    }

    return true
  }

  render() {
    const {
      label,
      name,
      value,
      popover,
      type='text',
      format=defaultFormat,
      parse,
      onChange,
      inputGroupBefore,
      inputGroupAfter,
      annotationLabel,
      subLabel,
      ...inputProps
    } = this.props
    const { initialValue, currentValue, calculatedValue, error, keepEmpty } = this.state

    const inputValue = !currentValue && calculatedValue && !keepEmpty ? calculatedValue : currentValue

    const InputComponent = type === 'select' ? Select : FormControl
    const formattedValue = format(inputValue, name)
    const handleChange = (event) => onChange(event, onChangeValue(event, { name, parse }))

    let inputField = <InputComponent
      value={formattedValue}
      onChange={handleChange}
      onFocus={this.handleOnFocus}
      onBlur={this.handleOnBlur}
      name={name}
      type={type}
      {...inputProps}
    />

    // Wrap input component in InputGroup if before or after addons.
    if (inputGroupBefore || inputGroupAfter) {
      inputField = (
        <InputGroup>
          {inputGroupBefore}
          {inputField}
          {inputGroupAfter}
        </InputGroup>
      )
    }

    let outerClass = currentValue === initialValue || currentValue === calculatedValue ? 'initial-value' : 'changed-value'
    if (typeof calculatedValue !== 'undefined') {
      outerClass += ' calculated-value'
    }

    const validationState = error ? 'error' : null

    return (
      <FormGroup controlId={name} className={outerClass} validationState={validationState}>
        {label && (
          <ControlLabel>
            {label}{annotationLabel && <sup>{annotationLabel}</sup>}
          </ControlLabel>
        )}
        {popover && <HelpPopover popover={popover} />}
        {subLabel && (
          <div className="sub-label">{subLabel}</div>
        )}
        {inputField}
        {error && (
          <HelpBlock>{error}</HelpBlock>
        )}
      </FormGroup>
    )
  }
}

Field.propTypes = {
  label: PropTypes.string,
  name: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  calculatedValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  popover: PropTypes.shape({
    title: PropTypes.string,
    body: PropTypes.arrayOf(PropTypes.string),
  }),
  type: PropTypes.string,
  format: PropTypes.func,
  parse: PropTypes.func,
  onChange: PropTypes.func,
  inputGroupBefore: PropTypes.node,
  inputGroupAfter: PropTypes.node,
  validator: PropTypes.oneOfType([PropTypes.func, PropTypes.arrayOf(PropTypes.func)]),
  onValidation: PropTypes.func,
  annotationLabel: PropTypes.node,
  subLabel: PropTypes.string
}

export default Field



// WEBPACK FOOTER //
// ./src/components/Field/index.js